/**
 * ToolTimeline
 *
 * Inline action timeline renderer for assistant messages.
 *
 * Primary responsibilities:
 * - Render action start/complete/error events inline
 * - Expand results for selected actions by default
 * - Display human-friendly action labels instead of raw identifiers
 *
 * Dependencies:
 * - Action results renderer
 * - Platform action label helpers (registry-aware)
 */
'use client'

import React, { useMemo, useState } from 'react'
import {
  IconAiAgents,
  IconApi,
  IconArchive,
  IconBrain,
  IconCalendar,
  IconChartBar,
  IconChecklist,
  IconChevronDown,
  IconClock,
  IconCode,
  IconCube,
  IconDatabase,
  IconDeviceLaptop,
  IconEdit,
  IconCpu,
  IconFileSpreadsheet,
  IconFileText,
  IconFlag,
  IconFolder,
  IconLayoutDashboard,
  IconLink,
  IconMail,
  IconMessages,
  IconMicrophone,
  IconNotes,
  IconPalette,
  IconPaperclip,
  IconPhoto,
  IconPresentation,
  IconRobot,
  IconSearch,
  IconTable,
  IconUsers,
  IconVideo,
  IconWorld
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { ToolTimelineEntry } from '@/types/annotation'
import {
  isMediaGenerationToolKey,
  renderToolResultForEntry,
  shouldDefaultExpandToolResult
} from '@/components/messages/annotations/tool-results'
import type { ToolActivityEntry } from '@/components/messages/annotations/tool-activity-log'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { getPlatformToolActionLabel } from '@/lib/platformTools/utils/platformToolLabels'
import { getToolDisplayLabel } from '@/lib/tool-labels'

interface ToolTimelineProps {
  entries?: ToolTimelineEntry[] | null
  className?: string
}

const ICON_CLASS = 'size-4 shrink-0 text-muted-foreground'

const INTERNAL_TOOL_TIMELINE_NAMES = new Set(['set_todos', 'update_todo_status'])

function getLosiSpacesToolIcon(name: string): React.ReactNode {
  const normalized = name.toLowerCase()

  if (normalized.includes('spacefile')) return <IconPaperclip className={ICON_CLASS} />
  if (normalized.includes('spacecollection')) return <IconArchive className={ICON_CLASS} />
  if (normalized.includes('spacesheet')) return <IconTable className={ICON_CLASS} />
  if (normalized.includes('spacesprint')) return <IconFlag className={ICON_CLASS} />
  if (normalized.includes('spaceevent') || normalized.includes('calendar')) {
    return <IconCalendar className={ICON_CLASS} />
  }
  if (normalized.includes('spacedesign') || normalized.includes('whiteboard')) {
    return <IconPalette className={ICON_CLASS} />
  }
  if (normalized.includes('spacenote')) return <IconNotes className={ICON_CLASS} />
  if (normalized.includes('spacefolder')) return <IconFolder className={ICON_CLASS} />
  if (normalized.includes('spacechat') || normalized.includes('conversation')) {
    return <IconMessages className={ICON_CLASS} />
  }
  if (normalized.includes('member') || normalized.includes('invite')) {
    return <IconUsers className={ICON_CLASS} />
  }
  if (normalized.includes('linkeditem') || normalized.includes('attachable')) {
    return <IconLink className={ICON_CLASS} />
  }
  if (
    normalized.includes('task') ||
    normalized.includes('checklist') ||
    normalized.includes('subtask')
  ) {
    return <IconChecklist className={ICON_CLASS} />
  }
  if (normalized.includes('space')) return <IconLayoutDashboard className={ICON_CLASS} />

  return <IconApi className={ICON_CLASS} />
}

function getToolIcon(name: string): React.ReactNode {
  if (name === 'set_todos' || name === 'update_todo_status') {
    return <IconChecklist className={ICON_CLASS} />
  }
  if (name === 'show_plan' || name === 'edit_canvas') {
    return <IconFileText className={ICON_CLASS} />
  }
  if (name === 'rename_chat') return <IconEdit className={ICON_CLASS} />
  if (name.startsWith('losiSpaces__')) return getLosiSpacesToolIcon(name)
  if (name.startsWith('VirtualFilesystem__')) return <IconFolder className={ICON_CLASS} />
  if (name.startsWith('MemoryTool__') || /memory/i.test(name))
    return <IconBrain className={ICON_CLASS} />
  if (name.startsWith('ChatManagementTool__') || name.startsWith('ChatSearch__')) {
    return <IconMessages className={ICON_CLASS} />
  }
  if (name.startsWith('sandbox__') || name === 'sandbox') {
    return <IconDeviceLaptop className={ICON_CLASS} />
  }
  if (name.startsWith('BookmarkSearch__') || name.startsWith('StarredMessagesSearch__')) {
    return <IconLink className={ICON_CLASS} />
  }
  if (name.startsWith('ConnectedServices__') || name.startsWith('ToolManagementTool__')) {
    return <IconLink className={ICON_CLASS} />
  }
  if (name.includes('googleSearch') || name.includes('FetchDataFromUrl')) {
    return <IconSearch className={ICON_CLASS} />
  }
  if (name.includes('getWeather') || name.includes('youtubeCaptions')) {
    return <IconWorld className={ICON_CLASS} />
  }
  if (/unifiedImage__|imageGenerator|gptImageFull|flux1Pro|stableDiffusion/i.test(name)) {
    return <IconPhoto className={ICON_CLASS} />
  }
  if (/videoGenerator|studioVideoGenerator/i.test(name)) {
    return <IconVideo className={ICON_CLASS} />
  }
  if (name.includes('yahooFinance') || name.includes('get_stock')) {
    return <IconChartBar className={ICON_CLASS} />
  }
  if (name.includes('Sidecar') || name === 'sidecar_run')
    return <IconRobot className={ICON_CLASS} />
  if (name === 'nexus_crm' || name === 'NexusCRMTool__run' || name.startsWith('crm__'))
    return <IconUsers className={ICON_CLASS} />
  if (name === 'nexus_booking' || name.includes('booking') || name.includes('appointment'))
    return <IconCalendar className={ICON_CLASS} />
  if (name.startsWith('nexus_') || name.startsWith('nexus__'))
    return <IconAiAgents className={ICON_CLASS} />
  if (/audioTool/i.test(name)) return <IconMicrophone className={ICON_CLASS} />
  if (/threeDAssetGenerator/i.test(name)) return <IconCube className={ICON_CLASS} />
  if (/currentTime/i.test(name)) return <IconClock className={ICON_CLASS} />
  if (/emailTool/i.test(name)) return <IconMail className={ICON_CLASS} />
  if (/smsTool/i.test(name)) return <IconMessages className={ICON_CLASS} />
  if (/slideCreator/i.test(name)) return <IconPresentation className={ICON_CLASS} />
  if (/documentGenerator/i.test(name)) return <IconFileText className={ICON_CLASS} />
  if (/spreadsheetTool/i.test(name)) return <IconFileSpreadsheet className={ICON_CLASS} />
  if (/codeInterpreter|codeExecutionTool/i.test(name))
    return <IconCode className={ICON_CLASS} />
  if (/dataAnalysisTool/i.test(name)) return <IconDatabase className={ICON_CLASS} />
  if (/ContentEngine__/i.test(name)) return <IconCpu className={ICON_CLASS} />
  if (/taskAutomation/i.test(name)) return <IconChecklist className={ICON_CLASS} />
  if (/websiteScreenshot/i.test(name)) return <IconPhoto className={ICON_CLASS} />
  if (/unifiedImage/i.test(name)) return <IconPhoto className={ICON_CLASS} />
  if (/modifyVideoTool/i.test(name)) return <IconVideo className={ICON_CLASS} />
  return <IconApi className={ICON_CLASS} />
}

function getStatusLabel(status: ToolTimelineEntry['status'], t: (key: string) => string): string {
  if (status === 'running') return t('Running')
  if (status === 'progress') return t('Running')
  if (status === 'error') return t('Failed')
  return t('Complete')
}

/** Progress copy that only restates status — hide it so rows stay one line. */
function isStatusNoiseProgressMessage(message?: string | null): boolean {
  if (!message) return true
  const normalized = message.trim().toLowerCase().replace(/\.+$/, '')
  if (!normalized) return true
  return /^(executing(\s+running)?|running|in progress|loading|working|searching|please wait)$/i.test(
    normalized
  )
}

function getMeaningfulProgress(entry: ToolTimelineEntry): {
  message?: string
  step?: string
  percent?: number
} | null {
  const progress = entry.progress
  if (!progress) return null

  const message =
    progress.message && !isStatusNoiseProgressMessage(progress.message)
      ? progress.message.trim()
      : undefined
  const step =
    progress.step && !isStatusNoiseProgressMessage(progress.step)
      ? progress.step.trim()
      : undefined
  const percent =
    typeof progress.percent === 'number' && Number.isFinite(progress.percent)
      ? progress.percent
      : undefined

  if (!message && !step && percent == null) return null
  return { message, step, percent }
}

function getTimelineEntryLabel(entry: ToolTimelineEntry): string {
  if (entry.status === 'complete') {
    return getToolDisplayLabel(entry.name, entry.result)
  }

  return getPlatformToolActionLabel(entry.name, entry.request)
}

function toToolActivityEntry(entry: ToolTimelineEntry): ToolActivityEntry {
  const durationMs =
    typeof entry.endedAt === 'number' && typeof entry.startedAt === 'number'
      ? Math.max(0, entry.endedAt - entry.startedAt)
      : 0

  return {
    key: entry.name,
    name: entry.name,
    durationMs,
    request: entry.request,
    result: entry.result,
    status: entry.status
  }
}

function isBrowserTimelineEntry(entry: ToolTimelineEntry) {
  return entry.name === 'sandbox__runAgentBrowser'
}

function formatResultPreview(result: unknown, maxLen: number): string {
  if (result === undefined || result === null) return ''

  if (typeof result === 'string') {
    try {
      const parsed = JSON.parse(result) as unknown
      return formatResultPreview(parsed, maxLen)
    } catch {
      return truncate(result, maxLen)
    }
  }

  if (typeof result === 'object') {
    const record = result as Record<string, unknown>
    const lines = Object.entries(record)
      .slice(0, 8)
      .map(([key, value]) => {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return `${key}: ${String(value)}`
        }

        if (Array.isArray(value)) {
          return `${key}: ${value.length} item${value.length === 1 ? '' : 's'}`
        }

        if (value && typeof value === 'object') {
          return `${key}: available`
        }

        return null
      })
      .filter((line): line is string => Boolean(line))

    if (lines.length > 0) {
      return truncate(lines.join('\n'), maxLen)
    }

    return 'Structured result available.'
  }

  return String(result)
}

function truncate(value: string, max: number): string {
  if (value.length <= max) return value
  return value.slice(0, max) + '…'
}

export function ToolTimeline({ entries, className }: ToolTimelineProps) {
  const { t } = useTranslation()
  const prefersReducedMotion = useReducedMotion()

  const timeline = useMemo(
    () =>
      (Array.isArray(entries) ? entries : []).filter(
        (entry) => !INTERNAL_TOOL_TIMELINE_NAMES.has(entry.name)
      ),
    [entries]
  )

  // User overrides ONLY. Default expansion is DERIVED at render time (below),
  // never synced into state via an effect. A prior effect seeded defaults with
  // setExpandedMap on every streaming `timeline` change, which — because
  // timeline identity churns each chunk — drove an unbounded
  // setState -> re-render -> effect -> setState loop (React #185). Removing the
  // effect makes the loop structurally impossible regardless of entry id churn.
  const [expandedOverrides, setExpandedOverrides] = useState<Record<string, boolean>>({})

  const isEntryExpanded = React.useCallback(
    (entry: ToolTimelineEntry): boolean => {
      const override = expandedOverrides[entry.id]
      if (override != null) return override
      // Running rows with no result yet stay collapsed — avoids "executing Running..." dumps.
      if (entry.status === 'running' || entry.status === 'progress') {
        const hasResult =
          entry.result != null &&
          !(typeof entry.result === 'string' && entry.result.trim() === '')
        if (!hasResult) return false
      }
      const activityEntry = toToolActivityEntry(entry)
      return Boolean(entry.isExpanded) || shouldDefaultExpandToolResult(activityEntry)
    },
    [expandedOverrides]
  )

  const renderedResultsById = useMemo(() => {
    const map: Record<string, React.ReactNode> = {}
    timeline.forEach((entry) => {
      if (isBrowserTimelineEntry(entry)) {
        map[entry.id] = null
        return
      }

      const activityEntry = toToolActivityEntry(entry)
      map[entry.id] = renderToolResultForEntry(activityEntry)
    })
    return map
  }, [timeline])

  if (timeline.length === 0) return null

  return (
    <div
      className={cn('not-prose w-full', className)}
      role="region"
      aria-label="Action activity timeline"
    >
      <ul className="flex flex-col gap-1">
        {timeline.map((entry) => {
          const isMediaTool = isMediaGenerationToolKey(entry.name)
          const renderedResult = renderedResultsById[entry.id]
          // Media tools always show the preview — no expand dump / Result summary.
          const expanded = isMediaTool && renderedResult ? true : isEntryExpanded(entry)
          const preview = formatResultPreview(entry.result, 400)
          const statusLabel = getStatusLabel(entry.status, t)
          const entryLabel = getTimelineEntryLabel(entry)
          const meaningfulProgress = getMeaningfulProgress(entry)
          const isLive = entry.status === 'running' || entry.status === 'progress'
          const showToggle = !(isMediaTool && renderedResult)

          return (
            <motion.li
              key={entry.id}
              layout="position"
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : { type: 'spring', stiffness: 460, damping: 34, mass: 0.7 }
              }
              className="rounded-md border border-transparent"
            >
              <button
                type="button"
                onClick={() => {
                  if (!showToggle) return
                  setExpandedOverrides((prev) => ({
                    ...prev,
                    [entry.id]: !isEntryExpanded(entry)
                  }))
                }}
                className={cn(
                  'text-muted-foreground hover:text-foreground focus-visible:ring-ring flex w-full items-center gap-2 py-1 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2',
                  !showToggle && 'cursor-default hover:text-muted-foreground'
                )}
                aria-expanded={expanded}
                disabled={!showToggle}
              >
                <span aria-hidden>{getToolIcon(entry.name)}</span>
                <span className="min-w-0 flex-1 truncate" title={entryLabel}>
                  {entryLabel}
                </span>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={`${entry.id}-${entry.status}`}
                    initial={prefersReducedMotion ? false : { opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.18 }}
                    className={cn(
                      'shrink-0 text-xs',
                      isLive ? 'text-primary' : 'text-muted-foreground'
                    )}
                  >
                    {statusLabel}
                  </motion.span>
                </AnimatePresence>
                {showToggle ? (
                  <IconChevronDown
                    className={cn(
                      'size-3.5 shrink-0 transition-transform',
                      expanded ? 'rotate-0' : '-rotate-90'
                    )}
                    aria-hidden
                  />
                ) : (
                  <span className="size-3.5 shrink-0" aria-hidden />
                )}
              </button>
              <AnimatePresence initial={false}>
                {expanded && (
                  <motion.div
                    initial={
                      prefersReducedMotion
                        ? false
                        : isMediaTool
                          ? { opacity: 0, y: 6 }
                          : { height: 0, opacity: 0 }
                    }
                    animate={
                      isMediaTool
                        ? { opacity: 1, y: 0 }
                        : { height: 'auto', opacity: 1 }
                    }
                    exit={
                      prefersReducedMotion
                        ? { opacity: 0 }
                        : isMediaTool
                          ? { opacity: 0, y: 4 }
                          : { height: 0, opacity: 0 }
                    }
                    transition={
                      prefersReducedMotion
                        ? { duration: 0 }
                        : isMediaTool
                          ? { type: 'spring', stiffness: 420, damping: 32, mass: 0.7 }
                          : { duration: 0.2, ease: [0.22, 1, 0.36, 1] }
                    }
                    className={cn(
                      'overflow-hidden text-sm',
                      isMediaTool
                        ? 'text-foreground pt-1.5'
                        : 'text-muted-foreground pl-6 pr-2 pt-1'
                    )}
                  >
                    {meaningfulProgress && !isMediaTool && (
                      <div className="mb-2 text-xs text-muted-foreground">
                        {meaningfulProgress.step && (
                          <span className="mr-2 font-medium">{meaningfulProgress.step}</span>
                        )}
                        {meaningfulProgress.message && (
                          <span>{meaningfulProgress.message}</span>
                        )}
                        {typeof meaningfulProgress.percent === 'number' && (
                          <span className="ml-2 tabular-nums">
                            {Math.round(meaningfulProgress.percent)}%
                          </span>
                        )}
                      </div>
                    )}
                    {renderedResult ? (
                      renderedResult
                    ) : preview && !isMediaTool ? (
                      <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">
                        {preview}
                      </pre>
                    ) : null}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.li>
          )
        })}
      </ul>
    </div>
  )
}
